import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

export interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog is dismissed (cancel, backdrop click, or escape). */
  onClose: () => void;
  /** Called when the user confirms the action. */
  onConfirm: () => void;
  /** Dialog title text. Defaults to "Confirm Action". */
  title?: string;
  /** Dialog body message. */
  message: string;
  /** Label for the confirm button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * Color of the confirm button.
   * Use 'error' for destructive actions (delete, discard).
   * Use 'primary' for non-destructive confirmations.
   * Defaults to 'error'.
   */
  confirmColor?: 'error' | 'primary';
}

/**
 * ConfirmDialog — A modal dialog used to confirm destructive actions or
 * warn about unsaved changes before proceeding.
 *
 * Used for:
 * - Delete confirmations (Requirement 14.8)
 * - DrawerPanel close with unsaved changes (Requirements 11.4, 11.5, 11.6)
 *
 * The dialog blocks interaction with the page behind it and requires
 * explicit user confirmation before any destructive action executes.
 *
 * @example
 * // Destructive delete confirmation
 * <ConfirmDialog
 *   open={showDelete}
 *   onClose={() => setShowDelete(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Machine"
 *   message="Are you sure you want to delete this machine? This action cannot be undone."
 *   confirmLabel="Delete"
 *   confirmColor="error"
 * />
 *
 * @example
 * // Unsaved changes warning (DrawerPanel integration)
 * <ConfirmDialog
 *   open={showDiscard}
 *   onClose={() => setShowDiscard(false)}
 *   onConfirm={handleDiscard}
 *   title="Unsaved Changes"
 *   message="You have unsaved changes. Are you sure you want to discard them?"
 *   confirmLabel="Discard"
 *   cancelLabel="Keep Editing"
 *   confirmColor="error"
 * />
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'error',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          autoFocus
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
